import { useState, useCallback, useEffect, useRef } from 'react';
import { Plus, X, Link } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

type DependencyType = 'FS' | 'SS' | 'FF' | 'SF';

interface Predecessor {
  taskId: string;
  taskRowNumber: number;
  taskName: string;
  type: DependencyType;
  lag: number; // In days, can be negative
}

interface AvailableTask {
  id: string;
  rowNumber: number;
  name: string;
}

interface PredecessorCellProps {
  value: Predecessor[];
  availableTasks: AvailableTask[];
  onChange: (value: Predecessor[]) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  currentTaskId: string;
  className?: string;
}

// Format: "3FS+2d" or "5SS-1d"
const formatPredecessor = (pred: Predecessor): string => {
  const lag =
    pred.lag !== 0
      ? `${pred.lag > 0 ? '+' : ''}${pred.lag}d`
      : '';
  return `${pred.taskRowNumber}${pred.type}${lag}`;
};

// Parse: "3FS+2d" -> { rowNumber: 3, type: 'FS', lag: 2 }
const parsePredecessorString = (
  str: string
): { rowNumber: number; type: DependencyType; lag: number } | null => {
  const match = str.match(/^(\d+)(FS|SS|FF|SF)?([+-]\d+d?)?$/i);
  if (!match) return null;

  const rowNumber = parseInt(match[1], 10);
  const type = (match[2]?.toUpperCase() as DependencyType) || 'FS';
  const lagStr = match[3] || '0';
  const lag = parseInt(lagStr.replace('d', ''), 10);

  return { rowNumber, type, lag };
};

export function PredecessorCell({
  value = [],
  availableTasks,
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  currentTaskId,
  className,
}: PredecessorCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter out current task from available tasks
  const selectableTasks = availableTasks.filter((t) => t.id !== currentTaskId);

  useEffect(() => {
    if (isEditing) {
      setIsOpen(true);
      setInputValue('');
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setIsOpen(false);
    }
  }, [isEditing]);

  const handleAddFromInput = useCallback(() => {
    const parsed = parsePredecessorString(inputValue.trim());
    if (!parsed) return;

    const task = selectableTasks.find(
      (t) => t.rowNumber === parsed.rowNumber
    );
    if (!task) return;

    // Check if already exists
    if (value.some((p) => p.taskId === task.id)) return;

    const newPred: Predecessor = {
      taskId: task.id,
      taskRowNumber: task.rowNumber,
      taskName: task.name,
      type: parsed.type,
      lag: parsed.lag,
    };

    onChange([...value, newPred]);
    setInputValue('');
  }, [inputValue, selectableTasks, value, onChange]);

  const handleAddFromSelect = useCallback(
    (taskId: string) => {
      const task = selectableTasks.find((t) => t.id === taskId);
      if (!task) return;

      if (value.some((p) => p.taskId === task.id)) return;

      const newPred: Predecessor = {
        taskId: task.id,
        taskRowNumber: task.rowNumber,
        taskName: task.name,
        type: 'FS',
        lag: 0,
      };

      onChange([...value, newPred]);
    },
    [selectableTasks, value, onChange]
  );

  const handleRemove = useCallback(
    (taskId: string) => {
      onChange(value.filter((p) => p.taskId !== taskId));
    },
    [value, onChange]
  );

  const handleUpdatePred = useCallback(
    (taskId: string, updates: Partial<Predecessor>) => {
      onChange(
        value.map((p) => (p.taskId === taskId ? { ...p, ...updates } : p))
      );
    },
    [value, onChange]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        onEndEdit();
      }
    },
    [onEndEdit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddFromInput();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
        onEndEdit();
      }
    },
    [handleAddFromInput, onEndEdit]
  );

  const displayValue = value.map(formatPredecessor).join(', ');

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'w-full h-full px-2 py-1 text-sm flex items-center cursor-pointer',
            'hover:bg-gray-50 truncate',
            isEditing && 'bg-white ring-2 ring-blue-500',
            !displayValue && 'text-gray-400',
            className
          )}
          onClick={onStartEdit}
        >
          {displayValue || (
            <span className="flex items-center gap-1">
              <Link className="h-3.5 w-3.5" />
              Add predecessor...
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b">
          <div className="text-sm font-medium mb-2">Add Predecessor</div>
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="e.g., 3FS+2d"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 px-2 py-1.5 text-sm border rounded outline-none focus:ring-1 focus:ring-blue-500"
            />
            <Button size="sm" onClick={handleAddFromInput}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Format: [row#][type][lag] (e.g., 3FS+2d, 5SS-1d)
          </div>
        </div>

        <div className="p-3 border-b">
          <div className="text-sm text-gray-500 mb-2">Or select a task:</div>
          <Select onValueChange={handleAddFromSelect}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select task..." />
            </SelectTrigger>
            <SelectContent>
              {selectableTasks
                .filter((t) => !value.some((p) => p.taskId === t.id))
                .map((task) => (
                  <SelectItem key={task.id} value={task.id}>
                    {task.rowNumber}. {task.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        {value.length > 0 && (
          <div className="p-3">
            <div className="text-sm font-medium mb-2">Current Predecessors</div>
            <div className="space-y-2">
              {value.map((pred) => (
                <div
                  key={pred.taskId}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {pred.taskRowNumber}. {pred.taskName}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Select
                        value={pred.type}
                        onValueChange={(type: DependencyType) =>
                          handleUpdatePred(pred.taskId, { type })
                        }
                      >
                        <SelectTrigger className="h-7 w-16 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FS">FS</SelectItem>
                          <SelectItem value="SS">SS</SelectItem>
                          <SelectItem value="FF">FF</SelectItem>
                          <SelectItem value="SF">SF</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-500 mr-1">Lag:</span>
                        <input
                          type="number"
                          value={pred.lag}
                          onChange={(e) =>
                            handleUpdatePred(pred.taskId, {
                              lag: parseInt(e.target.value, 10) || 0,
                            })
                          }
                          className="w-16 h-7 px-2 text-xs border rounded"
                        />
                        <span className="text-xs text-gray-500 ml-1">days</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="p-1 hover:bg-gray-200 rounded"
                    onClick={() => handleRemove(pred.taskId)}
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="p-2 border-t flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              onEndEdit();
            }}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default PredecessorCell;
